import { FC, ReactNode } from 'react'

import { Box, Card, Typography } from '@mui/material'

export interface ShowcaseDetailStat {
  value: ReactNode
  label: string
  /** Optional explicit value colour (e.g. motivation colour or a delta token). */
  color?: string
}

interface ShowcaseDetailHeaderProps {
  /** Drives the left accent border colour. */
  accent: 'motivation' | 'pattern'
  title: string
  badgeLabel: string
  /** Optional leading colour dot (used for motivations). */
  dotColor?: string
  desc?: ReactNode
  stats: ShowcaseDetailStat[]
  /** Optional slot rendered in the title row (e.g. the lens dropdown). */
  titleExtra?: ReactNode
}

/**
 * Hero header for the Motivation and Story Pattern detail views: accent border,
 * title + kind badge, description, and a row of stat tiles.
 */
export const ShowcaseDetailHeader: FC<ShowcaseDetailHeaderProps> = ({ accent, title, badgeLabel, dotColor, desc, stats, titleExtra }) => (
  <Card variant="outlined" sx={{ p: 2.5, borderLeft: '4px solid', borderLeftColor: accent === 'motivation' ? 'secondary.main' : 'primary.main' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
      {dotColor && <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 'bold' }}>{title}</Typography>
      <Box
        component="span"
        sx={{
          fontSize: '0.625rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          px: 1.25,
          py: 0.375,
          borderRadius: 100,
          bgcolor: accent === 'motivation' ? 'secondary.main' : 'primary.main',
          color: accent === 'motivation' ? 'secondary.contrastText' : 'primary.contrastText',
        }}
      >
        {badgeLabel}
      </Box>
      {titleExtra}
    </Box>
    {desc && (
      <Typography
        sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.6, maxWidth: 760, '& strong': { color: 'text.primary', fontWeight: 700 } }}
      >
        {desc}
      </Typography>
    )}
    {stats.length > 0 && (
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
        {stats.map((stat) => (
          <Box key={stat.label} sx={{ minWidth: 120, bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: '1.375rem', fontWeight: 'bold', lineHeight: 1, color: stat.color ?? 'text.primary' }}>{stat.value}</Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.5 }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Card>
)
